<template>
  <el-dialog
    :title="dialogTitle"
    :visible="dialogVisible"
    width="1180px"
    top="5vh"
    :before-close="closeDialog"
    :close-on-click-modal="false"
    custom-class="sk-dialog kids-lifecycle-dialog"
  >
    <div class="sk-dialog-content">
      <div class="sk-panel">
        <div class="sk-panel-head">
          <div class="sk-panel-icon" style="background: linear-gradient(135deg, #60a5fa, #2563eb)">
            <i class="el-icon-time"></i>
          </div>
          <div>
            <p class="sk-panel-title">Lịch sử chuyển đổi của học sinh</p>
            <p class="sk-panel-subtitle">
              Mã học sinh: {{ kid && kid.kidCode ? kid.kidCode : "-" }}
            </p>
          </div>
        </div>

        <!-- Bộ lọc của dialog: học sinh đã cố định, chỉ cần lọc sự kiện và khoảng ngày. -->
        <div class="lifecycle-dialog-filters">
          <el-select
            v-model="dataSearch.eventType"
            clearable
            filterable
            placeholder="Lọc theo sự kiện"
            class="lifecycle-dialog-filter-event"
            @change="handleFilterChange"
          >
            <el-option
              v-for="item in eventTypeList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <el-date-picker
            v-model="dataSearch.fromDate"
            type="date"
            value-format="yyyy-MM-dd"
            format="dd-MM-yyyy"
            clearable
            placeholder="Từ ngày"
            class="lifecycle-dialog-filter-date"
            @change="handleFilterChange"
          />
          <el-date-picker
            v-model="dataSearch.toDate"
            type="date"
            value-format="yyyy-MM-dd"
            format="dd-MM-yyyy"
            clearable
            placeholder="Đến ngày"
            class="lifecycle-dialog-filter-date"
            @change="handleFilterChange"
          />
          <el-button
            plain
            icon="el-icon-refresh"
            class="lifecycle-dialog-filter-reset"
            @click="resetFilters"
          >Đặt lại</el-button>
        </div>

        <el-table
          v-loading="loading"
          :data="eventList"
          border
          stripe
          class="sk-table lifecycle-dialog-table"
          max-height="480"
          empty-text="Chưa có lịch sử chuyển đổi"
          style="width: 100%"
        >
          <el-table-column label="Từ ngày" width="110" align="center">
            <template slot-scope="{ row }">
              {{ formatDate(row.startDate || row.effectiveDate) }}
            </template>
          </el-table-column>
          <el-table-column label="Đến ngày" width="110" align="center">
            <template slot-scope="{ row }">
              {{ formatEndDate(row.endDate) }}
            </template>
          </el-table-column>
          <el-table-column label="Sự kiện" width="155" align="center">
            <template slot-scope="{ row }">
              <el-tag :type="eventTagType(row.eventType)" size="small" effect="light">
                {{ eventLabel(row.eventType) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="Nội dung thay đổi" min-width="300" show-overflow-tooltip>
            <template slot-scope="{ row }">
              {{ eventDescription(row) }}
            </template>
          </el-table-column>
          <el-table-column label="Người thao tác" min-width="145" align="center">
            <template slot-scope="{ row }">
              {{ row.modifiedByName || row.lastModifieBy || "Không xác định" }}
            </template>
          </el-table-column>
          <el-table-column label="Thời gian thao tác" width="160" align="center">
            <template slot-scope="{ row }">
              {{ formatDateTime(row.lastModifieDate || row.effectiveAt || row.createdDate) }}
            </template>
          </el-table-column>
        </el-table>

        <div class="lifecycle-dialog-pagination">
          <el-pagination
            background
            :current-page="pageNumber"
            :page-sizes="$pageListDefaultNew"
            :page-size="maxPageItem"
            :total="total"
            layout="total, sizes, prev, pager, next"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
      </div>
    </div>

    <span slot="footer" class="dialog-footer sk-dialog-footer">
      <el-button class="btn-cancel" type="danger" size="medium" @click="closeDialog()">
        <i class="el-icon-circle-close" />
        <span>Đóng</span>
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import KidsLifecycleService from "@/services/KidsService/KidsLifecycleService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";

export default {
  name: "KidsLifecycleDialog",
  props: {
    dialogVisible: {
      type: Boolean,
      default: false,
    },
    // Truyền nguyên dòng học sinh để hiển thị tên/mã và lấy kidCode lọc API.
    kid: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      pageNumber: 1,
      maxPageItem: this.$pageSizeDefaultNew,
      total: 0,
      eventList: [],
      dataSearch: {
        eventType: "",
        fromDate: "",
        toDate: "",
      },
      eventTypeList: [
        {value: "CREATED", label: "Tạo mới"},
        {value: "STATUS_CHANGED", label: "Đổi trạng thái"},
        {value: "CLASS_CHANGED", label: "Chuyển lớp"},
        {value: "GROUP_CHANGED", label: "Chuyển nhóm"},
        {value: "GROUP_ADDED", label: "Thêm vào nhóm"},
        {value: "GROUP_REMOVED", label: "Ra khỏi nhóm"},
        {value: "RESTORED", label: "Khôi phục hồ sơ"},
        {value: "DELETED", label: "Xóa hồ sơ"},
      ],
      statusLabelMap: {
        STUDYING: "Đang học",
        STUDY_WAIT: "Chờ học",
        RESERVE: "Bảo lưu",
        LEAVE_SCHOOL: "Nghỉ học",
        OUT_SCHOOL: "Ra trường",
        DELETE: "Đã xóa",
      },
    };
  },
  computed: {
    dialogTitle() {
      const fullName = this.kid && this.kid.fullName ? this.kid.fullName : "Học sinh";
      return `Lịch sử học sinh: ${fullName}`;
    },
  },
  watch: {
    // Mỗi lần mở dialog phải tải lại từ trang đầu để không giữ dữ liệu của
    // học sinh trước đó hoặc trang đang xem trước đó.
    dialogVisible(value) {
      if (value) {
        this.resetFilters(false);
        this.pageNumber = 1;
        this.loadHistory();
      } else {
        this.eventList = [];
        this.total = 0;
      }
    },
  },
  methods: {
    /** Gọi API chỉ theo kidCode của học sinh đang được chọn trong danh sách. */
    async loadHistory() {
      if (!this.kid || !this.kid.kidCode) {
        this.eventList = [];
        this.total = 0;
        return;
      }
      this.loading = true;
      try {
        const response = await KidsLifecycleService.search({
          pageNumber: this.pageNumber,
          maxPageItem: this.maxPageItem,
          kidCode: this.kid.kidCode,
          eventType: this.dataSearch.eventType || undefined,
          fromDate: this.dataSearch.fromDate || undefined,
          toDate: this.dataSearch.toDate || undefined,
        });
        const payload = response && response.data ? response.data : {};
        this.eventList = Array.isArray(payload.data) ? payload.data : [];
        this.total = Number(payload.total) || 0;
      } catch (err) {
        this.eventList = [];
        this.total = 0;
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Không tải được lịch sử học sinh"
        );
      } finally {
        this.loading = false;
      }
    },

    handleSizeChange(size) {
      this.maxPageItem = size;
      this.pageNumber = 1;
      this.loadHistory();
    },

    handlePageChange(page) {
      this.pageNumber = page;
      this.loadHistory();
    },

    /** Đổi bộ lọc luôn quay về trang đầu và không làm mất kidCode hiện tại. */
    handleFilterChange() {
      if (this.dataSearch.fromDate && this.dataSearch.toDate
        && this.dataSearch.fromDate > this.dataSearch.toDate) {
        this.$message({
          message: "Từ ngày không được lớn hơn đến ngày",
          type: "warning",
        });
        return;
      }
      this.pageNumber = 1;
      this.loadHistory();
    },

    /** Xóa riêng các điều kiện lọc trong dialog, vẫn giữ đúng học sinh. */
    resetFilters(loadData = true) {
      this.dataSearch = {
        eventType: "",
        fromDate: "",
        toDate: "",
      };
      this.pageNumber = 1;
      if (loadData) {
        this.loadHistory();
      }
    },

    /** Đóng dialog qua component cha để cha quản lý trạng thái hiển thị. */
    closeDialog(done) {
      this.$emit("dialog-close");
      if (typeof done === "function") {
        done();
      }
    },

    eventLabel(type) {
      const item = this.eventTypeList.find(x => x.value === type);
      return item ? item.label : type || "Sự kiện khác";
    },

    eventTagType(type) {
      const tagMap = {
        CREATED: "success",
        STATUS_CHANGED: "warning",
        CLASS_CHANGED: "primary",
        GROUP_CHANGED: "primary",
        GROUP_ADDED: "success",
        GROUP_REMOVED: "danger",
        RESTORED: "success",
        DELETED: "danger",
      };
      return tagMap[type] || "info";
    },

    statusLabel(status) {
      return this.statusLabelMap[status] || status || "-";
    },

    eventDescription(event) {
      if (event.eventType === "CREATED") {
        return `Trạng thái: ${this.statusLabel(event.newStatus)}${this.formatTransitionTarget(event.newClassName, "Lớp")}`;
      }
      if (event.eventType === "STATUS_CHANGED") {
        return `${this.statusLabel(event.oldStatus)} → ${this.statusLabel(event.newStatus)}`;
      }
      if (event.eventType === "CLASS_CHANGED") {
        return `${event.oldClassName || "-"} → ${event.newClassName || "-"}`;
      }
      if (event.eventType === "GROUP_CHANGED") {
        return `${event.oldGroupName || "-"} → ${event.newGroupName || "-"}`;
      }
      if (event.eventType === "GROUP_ADDED") {
        return `Thêm vào nhóm: ${event.newGroupName || "-"}`;
      }
      if (event.eventType === "GROUP_REMOVED") {
        return `Ra khỏi nhóm: ${event.oldGroupName || "-"}`;
      }
      if (event.eventType === "RESTORED") {
        return `Khôi phục hồ sơ, trạng thái hiện tại: ${this.statusLabel(event.newStatus)}`;
      }
      if (event.eventType === "DELETED") {
        return "Hồ sơ được xóa mềm";
      }
      return event.note || "-";
    },

    formatTransitionTarget(value, label) {
      return value ? `, ${label}: ${value}` : "";
    },

    formatDate(value) {
      if (!value) return "-";
      const parts = String(value).substring(0, 10).split("-");
      return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : value;
    },

    formatEndDate(value) {
      return value ? this.formatDate(value) : "Hiện tại";
    },

    formatDateTime(value) {
      if (!value) return "-";
      return String(value).replace("T", " ").substring(0, 19);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/sk-dialog.scss";
@import "@/assets/style/sk-table.scss";

.lifecycle-dialog-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.lifecycle-dialog-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
}

.lifecycle-dialog-filter-event {
  width: 230px;
}

.lifecycle-dialog-filter-date {
  width: 160px;
}

.lifecycle-dialog-filter-reset {
  margin-left: auto;
}

.lifecycle-dialog-table {
  width: 100%;
}
</style>
